/**
 * FlashProfile models a single SPI flash chip. It owns the backing byte
 * buffer and the RDID/SFDP metadata, and decodes SPI commands sent via
 * `handleTransfer(write, readLen)` — returning `write.length + readLen`
 * bytes that mirror the MISO line for the transfer.
 *
 * This is deliberately a thin, testable layer: DemoAdapter delegates
 * every SPI op here, and unit tests drive FlashProfile directly.
 *
 * Addressing is modelled like a real > 16 MiB part: `addressBytes` is the
 * power-on mode, B7h/E9h switch modes (if `supportsEN4B`), a soft reset
 * (66h/99h) returns to the power-on mode, and the dedicated 4-byte opcodes
 * (13h, 12h, 21h, ...) always take 4 address bytes (if
 * `supportsFourByteOpcodes`). In 3-byte mode the address wraps at 16 MiB.
 */
class FlashProfile {
    constructor({
        id,
        name,
        description = "",
        rdid,
        sfdp = null,
        capacity,
        addressBytes = 3,
        pageSize = 256,
        eraseSectorSize = 4096,
        eraseOpcode = 0x20,
        initialData = null,
        supportsFourByteOpcodes = capacity > 0x1000000,
        supportsEN4B = capacity > 0x1000000,
    }) {
        if (!(rdid instanceof Uint8Array) || rdid.length !== 3) {
            throw new Error(`FlashProfile ${id}: rdid must be a Uint8Array of length 3`);
        }
        if (sfdp !== null && !(sfdp instanceof Uint8Array)) {
            throw new Error(`FlashProfile ${id}: sfdp must be a Uint8Array or null`);
        }
        if (addressBytes !== 3 && addressBytes !== 4) {
            throw new Error(`FlashProfile ${id}: addressBytes must be 3 or 4`);
        }

        this.id = id;
        this.name = name;
        this.description = description;
        this.rdid = rdid;
        this.sfdp = sfdp;
        this.capacity = capacity;
        this.addressBytes = addressBytes;
        this.addressMode = addressBytes;
        this.supportsFourByteOpcodes = supportsFourByteOpcodes;
        this.supportsEN4B = supportsEN4B;
        this.pageSize = pageSize;
        this.eraseSectorSize = eraseSectorSize;
        this.eraseOpcode = eraseOpcode;

        this.buffer = new Uint8Array(capacity).fill(0xFF);
        if (initialData) {
            this.buffer.set(initialData.subarray(0, capacity));
        }

        this.writeEnabled = false;
    }

    /**
     * Reset the internal buffer to all-0xFF (a freshly-erased flash).
     */
    resetBuffer() {
        this.buffer.fill(0xFF);
        this.writeEnabled = false;
        this.addressMode = this.addressBytes;
    }

    /**
     * Decode a single SPI transfer. `write` is what the master sends
     * (MOSI); `readLen` is how many dummy bytes are clocked in after
     * the write. Returns a Uint8Array of length `write.length + readLen`
     * representing MISO for the whole transfer.
     */
    handleTransfer(write, readLen) {
        const totalLen = write.length + readLen;
        // MISO is pulled high when the flash is not driving: default to 0xFF.
        const response = new Uint8Array(totalLen).fill(0xFF);

        if (!write || write.length === 0) {
            return response;
        }

        const opcode = write[0];

        switch (opcode) {
            case 0x9F: // JEDEC RDID
                this._writeBytes(response, write.length, this.rdid);
                break;

            case 0x5A: // Read SFDP: opcode + 3 addr + 1 dummy + data
                if (this.sfdp) {
                    const addr = (write[1] << 16) | (write[2] << 8) | write[3];
                    for (let i = 0; i < readLen; i++) {
                        const srcAddr = addr + i;
                        response[write.length + i] = srcAddr < this.sfdp.length
                            ? this.sfdp[srcAddr]
                            : 0xFF;
                    }
                }
                break;

            case 0x03: // Read Data
            case 0x13: // Read Data, 4-byte address
            {
                const addressBytes = this._addressBytesFor(opcode);
                if (addressBytes === null) break;
                const addr = this._readAddress(write, addressBytes);
                if (addr === null) break;
                for (let i = 0; i < readLen; i++) {
                    if (addr + i < this.capacity) {
                        response[write.length + i] = this.buffer[addr + i];
                    }
                }
                break;
            }

            case 0x02: // Page Program
            case 0x12: // Page Program, 4-byte address
            {
                if (!this.writeEnabled) break;
                const addressBytes = this._addressBytesFor(opcode);
                if (addressBytes === null) break;
                const addr = this._readAddress(write, addressBytes);
                if (addr === null) break;
                const dataOffset = 1 + addressBytes;
                const dataLen = write.length - dataOffset;
                for (let i = 0; i < dataLen; i++) {
                    if (addr + i < this.capacity) {
                        // Real NOR flash only clears bits; AND approximates this.
                        this.buffer[addr + i] &= write[dataOffset + i];
                    }
                }
                this.writeEnabled = false;
                break;
            }

            case 0x20: // 4KB sector erase
            case 0x52: // 32KB block erase
            case 0xD8: // 64KB block erase
            case 0x21: // 4KB sector erase, 4-byte address
            case 0x5C: // 32KB block erase, 4-byte address
            case 0xDC: // 64KB block erase, 4-byte address
            {
                if (!this.writeEnabled) break;
                const addressBytes = this._addressBytesFor(opcode);
                if (addressBytes === null) break;
                const addr = this._readAddress(write, addressBytes);
                if (addr === null) break;
                const size = { 0x20: 4096, 0x21: 4096, 0x52: 32768, 0x5C: 32768 }[opcode] ?? 65536;
                const start = addr & ~(size - 1);
                for (let i = 0; i < size && start + i < this.capacity; i++) {
                    this.buffer[start + i] = 0xFF;
                }
                this.writeEnabled = false;
                break;
            }

            case 0xC7: // Chip Erase
            case 0x60: // Chip Erase (alt)
                if (!this.writeEnabled) break;
                this.buffer.fill(0xFF);
                this.writeEnabled = false;
                break;

            case 0x06: // Write Enable
                this.writeEnabled = true;
                break;

            case 0x04: // Write Disable
                this.writeEnabled = false;
                break;

            case 0x05: // Read Status Register-1
            case 0x35: // Read Status Register-2
                for (let i = 0; i < readLen; i++) {
                    response[write.length + i] = 0x00;
                }
                break;

            case 0xB7: // Enter 4-byte address mode
                if (this.supportsEN4B) this.addressMode = 4;
                break;

            case 0xE9: // Exit 4-byte address mode
                if (this.supportsEN4B) this.addressMode = 3;
                break;

            case 0x66: // Enable Reset
                break;

            case 0x99: // Reset
                this.writeEnabled = false;
                this.addressMode = this.addressBytes;
                break;

            default:
                // Unknown opcode: leave response as 0xFF.
                break;
        }

        return response;
    }

    /**
     * Number of address bytes an opcode takes in the current mode, or
     * null if the flash doesn't support the opcode at all.
     */
    _addressBytesFor(opcode) {
        if ([0x13, 0x12, 0x21, 0x5C, 0xDC].includes(opcode)) {
            return this.supportsFourByteOpcodes ? 4 : null;
        }
        return this.addressMode;
    }

    _readAddress(write, addressBytes) {
        if (write.length < 1 + addressBytes) return null;
        let addr = 0;
        for (let i = 0; i < addressBytes; i++) {
            addr = (addr << 8) | write[1 + i];
        }
        return addr >>> 0;
    }

    _writeBytes(response, offset, data) {
        for (let i = 0; i < data.length; i++) {
            if (offset + i < response.length) {
                response[offset + i] = data[i];
            }
        }
    }
}

export { FlashProfile };
